/**
 * src/components/ui/Link.jsx — an inline text link.
 *
 * Distinct from Button: a link navigates (changes the URL or leaves the
 * page), a button acts. Renders react-router's `Link` for an internal path
 * (starting with `/`) and a plain `<a>` otherwise, so external/`mailto:`/
 * `tel:` links don't have to think about which to use — one component,
 * correct element either way. Per Nocturne's TextLink spec, weight (not
 * colour) marks this as a link: plain text-primary, always underlined,
 * never the accent colour — which is also why `.pill`/`.calendar-event`
 * status markers (already text+icon, not just colour) were never links to
 * begin with.
 */
import { Link as RouterLink } from 'react-router-dom';

/**
 * @param {{ to: string, children: React.ReactNode } & React.AnchorHTMLAttributes<HTMLAnchorElement>} props
 * @returns {JSX.Element}
 */
export function Link({ to, children, className, ...rest }) {
  const classes = ['ui-link', className].filter(Boolean).join(' ');
  if (to?.startsWith('/')) {
    return (
      <RouterLink to={to} className={classes} {...rest}>
        {children}
      </RouterLink>
    );
  }
  return (
    <a href={to} className={classes} {...rest}>
      {children}
    </a>
  );
}
